import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Award, Plus, Edit2, Power, PowerOff, Loader2, Link2 } from 'lucide-react';

const EMPTY = { reward_name: '', reward_description: '', required_points: 500, linked_service_id: '', terms_and_conditions: '', status: 'active' };

export default function RewardServicesPage({ user, onLogout }) {
  const [rewards, setRewards] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | EMPTY | row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/reward-services'),
        api.get('/services'),
      ]);
      setRewards(r.data || []);
      setServices((s.data || []).filter(x => x.status === 'active'));
    } catch (err) {
      toast.error('Could not load rewards');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const payload = { ...editing, required_points: parseInt(editing.required_points, 10) };
    if (!payload.reward_name?.trim()) return toast.error('Reward name is required');
    if (!payload.linked_service_id) return toast.error('Pick a linked service');
    if (!Number.isFinite(payload.required_points) || payload.required_points <= 0) return toast.error('Required points must be > 0');
    try {
      if (editing.id) await api.put(`/admin/reward-services/${editing.id}`, payload);
      else            await api.post('/admin/reward-services', payload);
      toast.success('Saved');
      setEditing(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const toggleStatus = async (r) => {
    try {
      await api.put(`/admin/reward-services/${r.id}`, { status: r.status === 'active' ? 'inactive' : 'active' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-600" />
              Reward Services
            </h1>
            <p className="text-slate-500 text-sm mt-1">Define the treatments patients can redeem with their points.</p>
          </div>
          <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" data-testid="reward-services-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Reward
          </Button>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : rewards.length === 0 ? (
          <Card className="p-10 text-center rounded-2xl border-dashed">
            <Award className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No reward services yet. Create your first one (suggestion: 3–5 rewards).</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rewards.map((r) => (
              <Card key={r.id} className="p-5 rounded-2xl border-slate-200 hover:shadow-md transition" data-testid={`reward-service-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{r.reward_name}</h3>
                      <Badge className={r.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-200 text-slate-600 border-slate-300'}>
                        {r.status}
                      </Badge>
                    </div>
                    {r.reward_description && <p className="text-xs text-slate-500 mt-1">{r.reward_description}</p>}
                    <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      {r.linked_service_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Points</div>
                    <div className="text-2xl font-bold text-amber-700">{(r.required_points || 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setEditing(r)} data-testid={`reward-service-edit-${r.id}`}>
                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => toggleStatus(r)} data-testid={`reward-service-toggle-${r.id}`}>
                    {r.status === 'active' ? <><PowerOff className="w-4 h-4 mr-1" /> Deactivate</> : <><Power className="w-4 h-4 mr-1" /> Activate</>}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {editing && (
          <Dialog open onOpenChange={() => setEditing(null)}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing.id ? 'Edit Reward' : 'Add Reward'}</DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-600">Reward name</Label>
                  <Input value={editing.reward_name || ''} onChange={(e) => setEditing({ ...editing, reward_name: e.target.value })} className="rounded-xl" data-testid="reward-form-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Description</Label>
                  <Textarea rows={2} value={editing.reward_description || ''} onChange={(e) => setEditing({ ...editing, reward_description: e.target.value })} className="rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Required points</Label>
                    <Input type="number" min="1" value={editing.required_points} onChange={(e) => setEditing({ ...editing, required_points: e.target.value })} className="rounded-xl" data-testid="reward-form-points" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Status</Label>
                    <Select value={editing.status || 'active'} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Linked service (will be booked when redeemed)</Label>
                  <Select value={editing.linked_service_id || ''} onValueChange={(v) => setEditing({ ...editing, linked_service_id: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="reward-form-service"><SelectValue placeholder="Choose service…" /></SelectTrigger>
                    <SelectContent>
                      {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Terms & conditions (optional)</Label>
                  <Textarea rows={2} value={editing.terms_and_conditions || ''} onChange={(e) => setEditing({ ...editing, terms_and_conditions: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" className="rounded-xl" onClick={() => setEditing(null)}>Cancel</Button>
                <Button className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" onClick={handleSave} data-testid="reward-form-save-btn">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
